import { useAppSelector } from '@app/core/hooks/redux-hooks';
import GridTextField from '@app/shared/components/grid-controls/grid-text-field/GridTextField';
import { Alignment } from '@app/shared/enums/alignment.enum';
import SvgChevron from '@app/shared/icons/Chevron';
import { FC } from 'react';
import styles from '../../../opm-inputs.module.scss';
import useLocalStorageCollapseState from '@app/core/hooks/useLocalStoreCollapse';
import { useLocale } from '@app/core/hooks/useLocale';
import CellValue from '@app/shared/components/cell-value/CellValue';
import { amountRules } from '@app/shared/components/cell-value/CellValueConfigurations';
import {
  numericBaseFieldFormattingProps,
  textFieldFormattingProps,
} from '@app/shared/components/form-controls/form-field/form-field-patterns';
import { getRequiredValidator } from '@app/core/validations/hook-forms/validators';
import Button from '@app/shared/components/button/Button';
import { ButtonAppearance, ButtonSize } from '@app/shared/components/button/button-enums';
import PlusSvg from '@app/shared/icons/Plus';
import MinusRoundedSvg from '@app/shared/icons/MinusRounded';
import { useFieldArray, useFormContext } from 'react-hook-form';
import {
  DebtItemDto,
  NetDebtItemValueDto,
  PerYearValuesDto,
} from '@app/shared/models/contracts/project-dto';
import { v4 as uuidv4 } from 'uuid';
import { getOpmNetDebtIndex } from '@app/core/store/project-slice-selectors';
import { CalcMethod, OpmInputDataSource } from '@app/shared/models/contracts/enums/shared-enums';
import { OpmInputFormModel2 } from '../../../OpmInputs';
import { Pill, PillSize, PillTheme } from '@app/shared/components/pill/Pill';
import SvgRevert from '@app/shared/icons/Revert';
import OpmInputEmptyHistoricCells from '../../OpmInputEmptyHistoricCells';
import {
  filterHistoricEventDatesFromFirstEvent,
  getMaxColumnCount,
} from '../../../utils/OpmInputHelper';

interface OpmNetDebtSectionProps {
  submitData: () => void;
  perYearCalculatedValuesFromPwerm: number[];
  initialTotalNetDebtFromPwerm: number;
}

const OpmNetDebtSection: FC<OpmNetDebtSectionProps> = ({
  submitData,
  perYearCalculatedValuesFromPwerm,
  initialTotalNetDebtFromPwerm,
}): JSX.Element => {
  const { control, setValue } = useFormContext<OpmInputFormModel2>();
  const project = useAppSelector((state) => state.project.projectDraft);
  const shouldGetValuesFromPwerm = project.details.calcMethod !== CalcMethod.OPM;
  const { l } = useLocale();
  const opmInputResults = useAppSelector((state) => state.opmCalculation.calculatedInputs);
  const showHistoricDataColumns = useAppSelector(
    (state) => state.uiValues.userSelections.opmInputs.showHistoricDataColumns
  );
  const historicColumnCount = Object.keys(
    filterHistoricEventDatesFromFirstEvent(opmInputResults?.historicEventDates ?? [])
  ).length;

  const [expanded, setExpanded] = useLocalStorageCollapseState('expanded-net-debt', project.id);

  const { replace: replaceNetDebtField } = useFieldArray({
    name: 'perYearInputs',
    control: control,
  });

  const { append: appendNetDebtField, remove: removeDebtField } = useFieldArray({
    name: 'netDebtItems',
    control: control,
  });

  const opmInputs = project.opmInput;
  const netDebtItems = opmInputs.netDebtItems ?? [];
  const hasEquityBridgeItems = (project.equityBridge?.netDebtItems.length ?? 0) > 0;
  const commonNetDebtItemIds = new Set(
    hasEquityBridgeItems
      ? project.equityBridge?.commonNetDebtItemIds ?? []
      : netDebtItems.map((netDebtItem) => netDebtItem.netDebtItemId)
  );
  const netDebtItemsWithIndices = netDebtItems
    .map((netDebtItem, formIndex) => ({
      netDebtItem,
      formIndex,
      isCommon: commonNetDebtItemIds.has(netDebtItem.netDebtItemId),
    }))
    .sort((first, second) => Number(second.isCommon) - Number(first.isCommon));
  const perYearInputs = opmInputs.perYearInputs;
  const pwermEnabled = project.details.calcMethod !== CalcMethod.OPM;

  const getNetDebtValueIndex = (
    yearInputs: PerYearValuesDto,
    netDebtItem: DebtItemDto,
    netDebtItemIndex: number
  ) => {
    const matchingIndex = getOpmNetDebtIndex(yearInputs, netDebtItem);
    return matchingIndex === -1 ? netDebtItemIndex : matchingIndex;
  };

  const toggleCollapse = () => {
    setExpanded(!expanded);
  };

  const maxColspanCount =
    project.opmInput.forecastYears !== null
      ? getMaxColumnCount(
          historicColumnCount,
          project.opmInput.forecastYears,
          showHistoricDataColumns
        )
      : 0;
  const titleRowContentColSpan = maxColspanCount - 1;

  const addNetDebt = () => {
    const netDebtItemId = uuidv4();
    appendNetDebtField(
      {
        netDebtItemId,
        historicalValue: null,
        narrative: '',
      } as DebtItemDto,
      {
        shouldFocus: false,
      }
    );

    replaceNetDebtField([
      ...(perYearInputs.map((forecast: PerYearValuesDto) => {
        return {
          ...forecast,
          netDebtValues: [
            ...(forecast.netDebtValues ?? []),
            { netDebtItemId, value: 0 } as NetDebtItemValueDto,
          ],
        };
      }) ?? []),
    ]);

    submitData();
  };

  const removeNetDebtItem =
    (netDebtItemToDelete: DebtItemDto, netDebtIndex: number) => (): void => {
      removeDebtField(netDebtIndex);
      replaceNetDebtField([
        ...(perYearInputs.map((forecast: PerYearValuesDto) => {
          return {
            ...forecast,
            netDebtValues: [
              ...forecast.netDebtValues.filter(
                (netDebtItem) => netDebtItem.netDebtItemId !== netDebtItemToDelete.netDebtItemId
              ),
            ],
          };
        }) ?? []),
      ]);

      submitData();
    };

  const totalNetDebtSource = perYearInputs.some(
    (pyi) => pyi.totalNetDebtSource === OpmInputDataSource.Override
  );
  const initialTotalNetDebtSource =
    opmInputs.initialTotalNetDebtSource === OpmInputDataSource.Override;
  const displayOverride =
    shouldGetValuesFromPwerm && (totalNetDebtSource || initialTotalNetDebtSource);

  const handleRevert = () => {
    setValue('initialTotalNetDebt', initialTotalNetDebtFromPwerm);
    opmInputs.perYearInputs.forEach((yi, index) => {
      setValue(`perYearInputs.${index}.totalNetDebt`, perYearCalculatedValuesFromPwerm[index]);
    });
    submitData();
  };

  return (
    <>
      <tbody>
        <tr>
          <th className={`table-primary__cell--header-tertiary ${styles[`opm-header`]}`}>
            <div className={styles['opm-collapsable-header']}>
              <button
                type="button"
                aria-label={l('_ToggleSectionCollapse', {
                  section: l('_NetDebt'),
                })}
                aria-expanded={expanded}
                style={{ border: 'none', backgroundColor: 'none', background: 'none' }}
                onClick={toggleCollapse}
                data-testid="nd-collapse-button">
                {!expanded ? (
                  <SvgChevron
                    onClick={toggleCollapse}
                    className={styles[`opm-chevron-collapsed`]}
                  />
                ) : (
                  <SvgChevron onClick={toggleCollapse} className={styles[`opm-chevron-expanded`]} />
                )}
              </button>
              {l('_NetDebt')}
            </div>
          </th>
          <th
            className={`table-primary__cell--header-tertiary ${styles[`opm-header-badge`]}`}
            colSpan={titleRowContentColSpan}>
            {project.details.calcMethod !== CalcMethod.OPM && (
              <div className={styles['opm-collapsable-header-badge']} data-testid="nd-badge">
                <Pill
                  size={PillSize.Xsmall}
                  noTextTransform
                  theme={displayOverride ? PillTheme.Orange : PillTheme.White}>
                  {displayOverride ? l('_Overridden') : l('_AutoPopulatedFromPWERM')}
                </Pill>
              </div>
            )}
          </th>
        </tr>
        {expanded && (
          <>
            {!pwermEnabled && (
              <>
                <tr>
                  <th
                    className={`table-primary__cell--vertical-separator ${
                      styles[`opm-table-rows`]
                    }`}>
                    {l('_CashNegative')}
                  </th>
                  <OpmInputEmptyHistoricCells indexIdentifier="cashValue" />
                  <td>
                    <GridTextField
                      name="initialCashValue"
                      data-testid="initialCashValue"
                      {...numericBaseFieldFormattingProps}
                      placeholder="0"
                      alignment={Alignment.Right}
                    />
                  </td>
                  {perYearInputs.map((_, index) => (
                    <td key={index + 'cashValue'}>
                      <GridTextField
                        name={`perYearInputs[${index}].cashValue`}
                        data-testid={`perYearInputs[${index}].cashValue`}
                        {...numericBaseFieldFormattingProps}
                        placeholder="0"
                        rules={{ ...getRequiredValidator() }}
                        alignment={Alignment.Right}
                      />
                    </td>
                  ))}
                  <td />
                </tr>
                {netDebtItemsWithIndices.map(({ netDebtItem, formIndex, isCommon }) => {
                  return (
                    <tr key={netDebtItem.netDebtItemId + 'debt-item'}>
                      <th
                        className={`table-primary__cell--vertical-separator ${
                          styles[`opm-table-rows`]
                        }`}>
                        <div
                          className={
                            isCommon ? styles['opm-common-net-debt-narrative'] : undefined
                          }>
                          <GridTextField
                            name={`netDebtItems[${formIndex}].narrative`}
                            data-testid={`netDebtItems[${formIndex}].narrative`}
                            rules={{ ...getRequiredValidator() }}
                            placeholder="Add narrative"
                            {...textFieldFormattingProps}
                          />
                        </div>
                      </th>
                      <OpmInputEmptyHistoricCells indexIdentifier="historicalValue" />
                      <td>
                        <GridTextField
                          name={`netDebtItems[${formIndex}].historicalValue`}
                          data-testid={`netDebtItems[${formIndex}].historicalValue`}
                          {...numericBaseFieldFormattingProps}
                          placeholder="0"
                          alignment={Alignment.Right}
                        />
                      </td>
                      {perYearInputs.map((yearInputs, index) => (
                        <td key={index + 'debt-item-values'}>
                          <GridTextField
                            name={`perYearInputs[${index}].netDebtValues[${getNetDebtValueIndex(
                              yearInputs,
                              netDebtItem,
                              formIndex
                            )}].value`}
                            data-testid={`perYearInputs[${index}].netDebtItems[${getNetDebtValueIndex(
                              yearInputs,
                              netDebtItem,
                              formIndex
                            )}].value`}
                            {...numericBaseFieldFormattingProps}
                            placeholder="0"
                            rules={{
                              ...getRequiredValidator(),
                            }}
                            alignment={Alignment.Right}
                          />
                        </td>
                      ))}
                      <td className="table-primary__cell--actions">
                        <Button
                          appearance={ButtonAppearance.CLEAN}
                          size={ButtonSize.TABLE_ACTION}
                          title={l('_DeleteItem')}
                          onClick={removeNetDebtItem(netDebtItem, formIndex)}>
                          <MinusRoundedSvg />
                        </Button>
                      </td>
                    </tr>
                  );
                })}
                <tr>
                  <th
                    className={`table-primary__cell--vertical-separator ${
                      styles[`opm-table-rows`]
                    }`}>
                    <div>
                      <Button
                        appearance={ButtonAppearance.DEFAULT_TERTIARY}
                        size={ButtonSize.TABLE_ACTION}
                        startIcon={<PlusSvg />}
                        onClick={() => addNetDebt()}>
                        {l('_AddItem')}
                      </Button>
                    </div>
                  </th>
                  <td colSpan={titleRowContentColSpan} />
                </tr>
              </>
            )}
            <tr data-testid="nd-row">
              <th className={`table-primary__cell--vertical-separator ${styles[`opm-table-rows`]}`}>
                {pwermEnabled ? l('_TotalNetDebt') : <strong>{l('_TotalNetDebt')}</strong>}
                {displayOverride && (
                  <SvgRevert
                    // stop the onBlur event occurring so we can control when the data is submitted
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => handleRevert()}
                    className={styles['revert-button']}
                  />
                )}
              </th>
              <OpmInputEmptyHistoricCells indexIdentifier="value" />
              <td data-testid="initialTotalNetDebt">
                {pwermEnabled ? (
                  <GridTextField
                    className={[
                      shouldGetValuesFromPwerm &&
                      opmInputs.initialTotalNetDebtSource === OpmInputDataSource.Override
                        ? styles['revertable-value']
                        : '',
                    ]}
                    name="initialTotalNetDebt"
                    data-testid="initialTotalNetDebt.value"
                    {...numericBaseFieldFormattingProps}
                    alignment={Alignment.Right}
                  />
                ) : (
                  <CellValue
                    dataTestId="initialTotalNetDebt.value"
                    {...amountRules}
                    strong={!pwermEnabled}
                    value={initialTotalNetDebtFromPwerm}
                  />
                )}
              </td>
              {perYearInputs.map((_, index) => (
                <td
                  key={index + 'totalNetDebt'}
                  data-testid={`perYearInputs[${index}].totalNetDebt`}
                  className={index === 0 ? styles[`padding-right-index0`] : ''}>
                  {pwermEnabled ? (
                    <GridTextField
                      className={[
                        shouldGetValuesFromPwerm &&
                        perYearInputs[index].totalNetDebtSource === OpmInputDataSource.Override
                          ? styles['revertable-value']
                          : '',
                      ]}
                      name={`perYearInputs.${index}.totalNetDebt`}
                      data-testid={`perYearInputs.${index}.totalNetDebt.value`}
                      {...numericBaseFieldFormattingProps}
                      alignment={Alignment.Right}
                    />
                  ) : (
                    <CellValue
                      dataTestId={`perYearInputs.${index}.totalNetDebt`}
                      {...amountRules}
                      strong={!pwermEnabled}
                      value={perYearCalculatedValuesFromPwerm[index]}
                    />
                  )}
                </td>
              ))}
              <td />
            </tr>
          </>
        )}
      </tbody>
    </>
  );
};

export default OpmNetDebtSection;
